<template>
  <div class="h-full overflow-x-auto overflow-y-auto">
    <!-- 筛选器 -->
    <div class="mb-4 flex gap-4">
      <select v-model="selectedCategory"
        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        @change="handleFilter">
        <option value="">全部分类</option>
        <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <select v-model="selectedApp"
        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
        @change="handleFilter">
        <option value="">全部应用</option>
        <option v-for="option in appNameOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- 表格头部 -->
    <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-200 dark:border-gray-700">
      <div class="flex min-w-[1000px]">
        <div class="w-16 py-3.5 px-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</div>
        <div class="flex-1 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">功能名称</div>
        <div class="w-24 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">分类</div>
        <div class="w-24 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">应用</div>
        <div class="w-32 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">默认配额类型</div>
        <div class="w-48 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">描述</div>
        <div class="w-32 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</div>
      </div>
    </div>

    <!-- 表格内容 -->
    <div class="divide-y divide-gray-200 dark:divide-gray-700">
      <div v-for="feature in filteredFeatures" :key="feature.id"
        class="flex items-center min-w-[1000px] hover:bg-indigo-50 dark:hover:bg-indigo-900/30">
        <div class="w-16 py-4 px-3 text-sm text-gray-900 dark:text-gray-100">{{ feature.id }}</div>
        <div class="flex-1 px-3 py-4 text-sm">
          <div class="text-gray-900 dark:text-gray-100 font-medium">{{ displayLocalizedText(feature.name) }}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{{ feature.featureCode }}</div>
        </div>
        <div class="w-24 px-3 py-4 text-sm">
          <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap"
            :class="getCategoryBadgeClass(feature.category)">
            {{ categoryLabels[feature.category] }}
          </span>
        </div>
        <div class="w-24 px-3 py-4 text-sm">
          <span
            class="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 whitespace-nowrap">
            {{ appNameLabels[feature.appName] }}
          </span>
        </div>
        <div class="w-32 px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
          {{ quotaTypeLabels[feature.defaultQuotaType] }}
        </div>
        <div class="w-48 px-3 py-4 text-sm text-gray-500 dark:text-gray-400 truncate" :title="displayLocalizedText(feature.description)">
          {{ displayLocalizedText(feature.description) || '-' }}
        </div>
        <div class="w-32 px-3 py-4 text-sm flex gap-2">
          <button class="link-btn-edit whitespace-nowrap"
            @click="handleEdit(feature)" title="编辑">
            编辑
          </button>
          <button class="link-btn-delete whitespace-nowrap"
            @click="handleDelete(feature)" title="删除">
            删除
          </button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="filteredFeatures.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
      暂无功能数据
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import {
  FeatureCategoryLabels,
  AppNameLabels,
  QuotaTypeLabels,
  getFeatureCategoryOptions,
  getAppNameOptions,
  FeatureCategory,
} from '@/types/membership/enums'
import type { Feature } from '@/types/membership/feature'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * Props
 */
interface Props {
  features: Feature[]
}

const props = defineProps<Props>()

/**
 * Emits
 */
const emit = defineEmits<{
  edit: [feature: Feature]
  delete: [feature: Feature]
}>()

/**
 * 筛选状态
 */
const selectedCategory = ref<string>('')
const selectedApp = ref<string>('')

/**
 * 标签映射
 */
const categoryLabels = FeatureCategoryLabels
const appNameLabels = AppNameLabels
const quotaTypeLabels = QuotaTypeLabels

/**
 * 选项列表
 */
const categoryOptions = getFeatureCategoryOptions()
const appNameOptions = getAppNameOptions()

/**
 * 筛选后的功能列表
 */
const filteredFeatures = computed(() => {
  return props.features.filter((feature) => {
    const categoryMatch = !selectedCategory.value || feature.category === selectedCategory.value
    const appMatch = !selectedApp.value || feature.appName === selectedApp.value
    return categoryMatch && appMatch
  })
})

/**
 * 获取分类徽章样式
 */
const getCategoryBadgeClass = (category: FeatureCategory) => {
  const classMap: Record<FeatureCategory, string> = {
    [FeatureCategory.SPEAKING]: 'bg-purple-50 text-purple-700',
    [FeatureCategory.WRITING]: 'bg-green-50 text-green-700',
    [FeatureCategory.LISTENING]: 'bg-blue-50 text-blue-700',
    [FeatureCategory.READING]: 'bg-yellow-50 text-yellow-700',
    [FeatureCategory.MOCK]: 'bg-red-50 text-red-700',
    [FeatureCategory.COMMON]: 'bg-gray-50 text-gray-700',
  }
  return classMap[category] || 'bg-gray-50 text-gray-700'
}

/**
 * 处理筛选
 */
const handleFilter = () => {
  // 筛选逻辑已通过 computed 实现
}

/**
 * 处理编辑
 */
const handleEdit = (feature: Feature) => {
  emit('edit', feature)
}

/**
 * 处理删除
 */
const handleDelete = (feature: Feature) => {
  emit('delete', feature)
}
</script>
